import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useAddCredentialsToTelegramAccountMutation,
} from '../store/api/authApi';

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export function TelegramLoginAddCredentialsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [addCredentials, { isLoading }] = useAddCredentialsToTelegramAccountMutation();

  const telegramId = searchParams.get('telegramId');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  });

  useEffect(() => {
    if (!telegramId) {
      navigate('/login');
    }
  }, [telegramId, navigate]);

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return t('validation.email.required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.email.invalid');
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('validation.password.required');
    }
    if (password.length < 8) {
      return t('validation.password.minLength');
    }
    if (password.length > 128) {
      return t('validation.password.maxLength');
    }
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string): string | undefined => {
    if (!confirmPassword) {
      return t('auth.register.confirmPasswordRequired');
    }
    if (confirmPassword !== formData.password) {
      return t('auth.register.passwordsDoNotMatch');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword);
    if (confirmPasswordError) {
      setErrors((prev) => ({ ...prev, confirmPassword: confirmPasswordError }));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !confirmPasswordError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    setTouched({ email: true, password: true, confirmPassword: true });

    if (!validateForm()) {
      return;
    }

    if (!telegramId) {
      navigate('/login');
      return;
    }

    try {
      await addCredentials({
        telegramId: Number.parseInt(telegramId, 10),
        email: formData.email.trim(),
        password: formData.password,
      }).unwrap();

      // Navigate to dashboard after successful credentials addition
      navigate('/dashboard');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setLocalError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }

    if (touched[name as keyof typeof touched]) {
      if (name === 'email') {
        const error = validateEmail(value);
        setErrors((prev) => ({
          ...prev,
          email: error,
        }));
      } else if (name === 'password') {
        const error = validatePassword(value);
        setErrors((prev) => ({
          ...prev,
          password: error,
        }));
      } else if (name === 'confirmPassword') {
        const error = validateConfirmPassword(value);
        setErrors((prev) => ({
          ...prev,
          confirmPassword: error,
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'email') {
      const error = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: error,
      }));
    } else if (name === 'password') {
      const error = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: error,
      }));
    } else if (name === 'confirmPassword') {
      const error = validateConfirmPassword(value);
      setErrors((prev) => ({
        ...prev,
        confirmPassword: error,
      }));
    }
  };

  if (!telegramId) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">

      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          <div className="text-center space-y-2 animate-slide-down">
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.telegramLogin.addCredentialsTitle')}
            </h1>
            <p className="text-neutral-medium font-body">
              {t('auth.telegramLogin.addCredentialsSubtitle')}
            </p>
          </div>

          {localError && (
            <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
              <p className="text-red-300 text-sm font-body">{localError}</p>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
            <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <label
                htmlFor="email"
                className="block text-sm font-body font-medium text-neutral-light"
              >
                {t('auth.register.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                  errors.email && touched.email
                    ? 'border-danger focus:ring-danger focus:border-danger'
                    : 'border-neutral-dark focus:border-brand-accent'
                }`}
                placeholder="user@example.com"
              />
              {errors.email && touched.email && (
                <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <label
                htmlFor="password"
                className="block text-sm font-body font-medium text-neutral-light"
              >
                {t('auth.register.password')}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                  errors.password && touched.password
                    ? 'border-danger focus:ring-danger focus:border-danger'
                    : 'border-neutral-dark focus:border-brand-accent'
                }`}
                placeholder="••••••••"
              />
              {errors.password && touched.password && (
                <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                  {errors.password}
                </p>
              )}
            </div>

            <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-body font-medium text-neutral-light"
              >
                {t('auth.register.confirmPassword')}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                  errors.confirmPassword && touched.confirmPassword
                    ? 'border-danger focus:ring-danger focus:border-danger'
                    : 'border-neutral-dark focus:border-brand-accent'
                }`}
                placeholder="••••••••"
              />
              {errors.confirmPassword && touched.confirmPassword && (
                <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up"
              style={{ animationDelay: '0.4s' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.telegramLogin.addCredentials')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

